import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, TrendingDown, TrendingUp } from "lucide-react";
import heroImg from "@/assets/hero-pos.jpg";
import { useLandingContent } from "@/features/admin/content/landingContent";
import { usePublicBusinessStats } from "../hooks/usePublicBusinessStats";
import { useHeroStats } from "../hooks/useHeroStats";
import { VideoModal } from "./VideoModal";

export function Hero() {
  const { hero } = useLandingContent();
  const [demoOpen, setDemoOpen] = useState(false);
  const isExternal = (h: string) => /^https?:\/\//.test(h);

  // KPIs de escaparate (inventados, coherentes con la hora del día — heroStats.ts).
  const stats = useHeroStats();
  const salesUp = stats.vsYesterdayPct >= 0;

  // Prueba social REAL desde Zeus (query pública): número exacto de negocios
  // activos + fotos de los últimos QUE TIENEN foto. Si el gateway no responde,
  // el texto cae al del CMS y no hay avatares.
  const { totalItems, photos } = usePublicBusinessStats(3);
  const proofText =
    totalItems != null
      ? `+${totalItems.toLocaleString("es-ES")} negocios activos`
      : hero.socialProofText;

  // Un logo presente pero roto (404) dejaría el icono partido del navegador:
  // se oculta ese avatar y ya. El estado se llena desde el `onError` de la
  // imagen (evento, no efecto).
  const [fotosRotas, setFotosRotas] = useState<string[]>([]);
  const marcarFotoRota = (id: string) =>
    setFotosRotas((rotas) => (rotas.includes(id) ? rotas : [...rotas, id]));
  const avatares = photos.filter((b) => !fotosRotas.includes(b.id));
  return (
    <section className="relative pt-12 lg:pt-20 pb-20 lg:pb-32 overflow-hidden">
      {/* Cinematic layered backdrop */}
      <div className="absolute inset-0 -z-20 bg-gradient-to-b from-white via-white to-neutral-50" />
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 size-[820px] rounded-full bg-[radial-gradient(closest-side,rgba(0,98,255,0.18),transparent)] blur-2xl animate-aurora" />
        <div className="absolute top-32 -left-32 size-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(99,102,241,0.16),transparent)] blur-2xl animate-aurora [animation-delay:-6s]" />
        <div className="absolute top-10 -right-24 size-[480px] rounded-full bg-[radial-gradient(closest-side,rgba(16,185,129,0.14),transparent)] blur-2xl animate-aurora [animation-delay:-3s]" />
        <div
          className="absolute inset-0 opacity-[0.05] animate-grid-pan"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.6) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="space-y-7 lg:space-y-8 animate-fade-up">
          {hero.badgeHref ? (
            isExternal(hero.badgeHref) ? (
              <a
                href={hero.badgeHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-wider uppercase hover:bg-primary/20 transition-colors"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                </span>
                {hero.badge}
              </a>
            ) : (
              <Link
                to={hero.badgeHref}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-wider uppercase hover:bg-primary/20 transition-colors"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                </span>
                {hero.badge}
              </Link>
            )
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-wider uppercase">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
              </span>
              {hero.badge}
            </div>
          )}

          <h1 className="text-[44px] leading-[1.02] sm:text-6xl lg:text-[88px] font-bold tracking-[-0.02em]">
            {hero.titleLead}
            <br />
            <span className="text-primary">{hero.titleHighlight}</span>
          </h1>

          <p className="text-lg lg:text-xl text-muted-foreground max-w-xl">
            {hero.subtitle}
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/registro" className="group inline-flex items-center gap-2 px-7 py-4 bg-foreground text-background font-semibold rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20">
              {hero.primaryCtaLabel}
              <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 px-7 py-4 bg-white/70 backdrop-blur border border-black/10 font-semibold rounded-xl hover:bg-white transition-all"
            >
              <Play className="size-4" />
              {hero.secondaryCtaLabel}
            </button>
          </div>

          <div className="flex items-center gap-5 pt-2 text-xs text-muted-foreground">
            {/* Logos REALES de los últimos negocios con foto (los filtra Zeus).
                Sin fotos no se pinta nada: tres círculos de colores que no son
                nadie no son prueba social, son ruido. */}
            {avatares.length > 0 && (
              <div className="flex -space-x-2">
                {avatares.map((b) => (
                  <img
                    key={b.id}
                    src={b.logo}
                    alt={b.legalName}
                    width={28}
                    height={28}
                    loading="lazy"
                    onError={() => marcarFotoRota(b.id)}
                    className="size-7 rounded-full border-2 border-white object-cover bg-neutral-100"
                  />
                ))}
              </div>
            )}
            <span>{proofText}</span>
          </div>
        </div>

        <div className="relative animate-fade-up [animation-delay:200ms]">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={heroImg}
              alt="Dashboard TecoPOS"
              width={1280}
              height={1024}
              className="w-full aspect-[5/4] object-cover"
            />
          </div>

          {/* Floating KPI card */}
          <div className="absolute -bottom-6 -left-4 lg:-left-8 bg-white p-5 rounded-2xl shadow-xl border border-black/5 space-y-3 max-w-[220px] animate-float">
            <div className="flex items-center gap-2">
              <div
                className={`size-8 rounded-lg grid place-items-center ${
                  salesUp
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {salesUp ? (
                  <TrendingUp className="size-4" />
                ) : (
                  <TrendingDown className="size-4" />
                )}
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Ventas hoy</p>
                <p className="text-lg font-bold leading-tight">
                  ${stats.salesToday.toLocaleString("en-US")}
                </p>
              </div>
            </div>
            <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.round(stats.dayProgress * 100)}%` }}
              />
            </div>
            <p
              className={`text-[10px] font-semibold ${
                salesUp ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {salesUp ? "+" : ""}
              {stats.vsYesterdayPct}% vs ayer
            </p>
          </div>

          {/* Floating product card */}
          <div className="hidden md:flex absolute -top-6 -right-4 lg:-right-8 bg-white p-4 rounded-2xl shadow-xl border border-black/5 items-center gap-3 animate-float [animation-delay:1s]">
            <div className="size-10 rounded-lg bg-primary/10 grid place-items-center text-primary text-sm font-bold">
              T
            </div>
            <div>
              <p className="text-xs font-bold">Tarjeta cargada</p>
              <p className="text-[10px] text-muted-foreground">
                +${stats.recharge.amount.toFixed(2)} · hace{" "}
                {stats.recharge.minutesAgo} min
              </p>
            </div>
          </div>
        </div>
      </div>

      <VideoModal open={demoOpen} url={hero.demoVideoUrl} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
